import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Plus,
  RefreshCw,
  GitBranch,
  Droplet,
  Anchor,
  CheckCircle2,
  Check,
  Phone,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { CTAButton } from "@/components/CTAButton";
import {
  Section,
  SectionHeading,
  Breadcrumb,
  ProcessSteps,
  InfoGrid,
  ProductDetailSection,
  SuppliedIncludedSection,
  CTABanner,
} from "@/components/Sections";
import { PRODUCTS, BUSINESS } from "@/lib/site";

const SERVICES_ITEMS = [
  { icon: "Plus", title: "New AC Installation", text: "Installation of new wall-mounted split air-conditioning systems." },
  { icon: "RefreshCw", title: "AC Replacement", text: "Removal and replacement of older air-conditioning units." },
  { icon: "GitBranch", title: "New Pipework", text: "New refrigerant pipework where required." },
  { icon: "Droplet", title: "Drainage", text: "Correct condensate drain routing and drainage setup." },
  { icon: "Anchor", title: "Brackets", text: "Outdoor condenser brackets where required." },
  { icon: "CheckCircle2", title: "Vacuum & Commissioning", text: "Proper system preparation, vacuuming and final operating checks before completion." },
];
const ICONS = { Plus, RefreshCw, GitBranch, Droplet, Anchor, CheckCircle2 };

const STEPS = [
  { title: "Assessment", text: "Assess the room, existing setup and installation requirements." },
  { title: "Recommendation", text: "Recommend suitable AC sizing and the appropriate installation approach." },
  { title: "Installation", text: "Professional fitting, pipework, drainage, connections, vacuuming and setup." },
  { title: "Final Check", text: "Test the system, confirm correct operation and leave the installation area clean." },
];

const WORKMANSHIP = [
  "Clean pipe routing",
  "Neat trunking where required",
  "Secure mounting",
  "Correct drainage",
  "Professional finishing",
  "Final operational checks",
];

export default function Installation() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.replace("#", ""));
    if (!el) return;
    const t = setTimeout(() => {
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
      else el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <>
      <SEO
        title="AC Installation Paphos | NorthWork Cyprus"
        description="Professional air-conditioning installation in Paphos for homes, apartments, villas, offices and commercial properties. Clean pipework, drainage and finishing by NorthWork Cyprus."
        path="/ac-installation-paphos"
        schema={{ "@type": "Service", serviceType: "AC Installation", areaServed: "Paphos" }}
      />
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "AC Installation" }]} />

      {/* Hero */}
      <section data-testid="page-hero" className="relative isolate overflow-hidden border-b border-border">
        {/* Full-width background image */}
        <img
          src="/images/installation-hero-bg.webp"
          alt="NorthWork Cyprus technician installing a wall-mounted split air-conditioning unit with outdoor condenser in Paphos"
          loading="eager"
          className="absolute inset-0 -z-20 h-full w-full object-cover object-[78%_28%] md:object-right"
        />
        {/* Dark overlay: dark behind text, lighter over the technician/AC on mobile so the image stays visible */}
        <div
          className="absolute inset-0 -z-10 bg-gradient-to-r from-[#0b0e14]/95 via-[#0b0e14]/62 to-[#0b0e14]/20 md:via-[#0b0e14]/80 md:to-[#0b0e14]/40"
          aria-hidden="true"
        />
        <div className="container-x grid items-center gap-10 py-8 md:py-12 lg:grid-cols-12 lg:gap-14 lg:py-20">
          <div className="lg:col-span-7">
            <Reveal variant="up">
              <h1 className="font-heading text-[2.7rem] font-medium leading-[1.05] tracking-tight text-white md:text-5xl md:leading-[1.04] lg:text-[4rem]">
                AC Installation<br /> in Paphos
              </h1>
            </Reveal>
            <Reveal delay={0.1} variant="up" className="mt-4 max-w-xl md:mt-6">
              <p className="text-base leading-relaxed text-white/85 lg:text-lg">
                Professional air-conditioning installation for homes, apartments, villas, offices
                and commercial properties across Paphos.
              </p>
            </Reveal>
            <Reveal delay={0.15} variant="up" className="mt-6 flex flex-col gap-3 sm:flex-row md:mt-8">
              <CTAButton to="/contact" testid="hero-primary-cta">
                Request Installation
              </CTAButton>
              <CTAButton href={`tel:${BUSINESS.phoneTel}`} variant="secondary" icon={Phone} testid="hero-call-cta" className="hidden md:inline-flex">
                {BUSINESS.phoneDisplay}
              </CTAButton>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Overview */}
      <Section className="py-16 lg:py-24">
        <div className="container-x grid gap-8 lg:grid-cols-12 lg:items-center lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-heading text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
                Professional AC Installation
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1} className="space-y-5 text-base leading-relaxed text-charcoal-muted lg:text-lg">
              <p>
                A quality air-conditioning system depends on more than the unit itself. Correct
                positioning, secure mounting, proper pipework, drainage, electrical connections,
                vacuuming and final setup all contribute to reliable long-term operation.
              </p>
              <p>
                NorthWork Cyprus provides professional installation with careful attention to both
                technical performance and the finished appearance of the job.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="mt-8">
              <h3 className="font-heading text-lg font-semibold tracking-tight text-charcoal">
                Installation Standards
              </h3>
              <div className="mt-4 grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                {WORKMANSHIP.map((item) => (
                  <div key={item} className="flex items-center gap-3 border-b border-border py-2.5">
                    <Check className="h-4 w-4 shrink-0 text-brand" strokeWidth={2.5} />
                    <span className="text-[14px] text-charcoal">{item}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </Section>

      {/* Installation services */}
      <Section className="border-t border-border bg-surface py-24 lg:py-32">
        <div className="container-x">
          <SectionHeading title="Installation Services" />
          <div className="mt-14">
            <InfoGrid items={SERVICES_ITEMS} columns={3} iconMap={ICONS} testid="installation-services" />
          </div>
        </div>
      </Section>

      {/* Supply & Installation — products, models & pricing */}
      <Section className="border-t border-border bg-surface py-16 lg:py-24">
        <div className="container-x">
          <SectionHeading
            title="Systems We Supply & Install"
            subtitle="NorthWork Cyprus supplies and installs selected AUX and GREE air-conditioning systems, professionally fitted for homes and businesses across Paphos."
          />
          <div className="mt-10 space-y-16 lg:mt-12 lg:space-y-20">
            <ProductDetailSection product={PRODUCTS[0]} />
            <ProductDetailSection product={PRODUCTS[1]} reverse />
          </div>
          <div className="mt-12 border-t border-border pt-12 lg:mt-14 lg:pt-14">
            <SuppliedIncludedSection />
          </div>
        </div>
      </Section>

      {/* Process */}
      <Section className="py-16 lg:py-24">
        <div className="container-x">
          <SectionHeading title="Installation Process" />
          <div className="mt-10">
            <ProcessSteps steps={STEPS} compact testid="installation-process" />
          </div>
        </div>
      </Section>

      <CTABanner
        title="Planning a New AC Installation?"
        text="Contact NorthWork Cyprus to discuss your installation requirements across Paphos and surrounding areas."
        primaryLabel="Request Installation"
        primaryTo="/contact"
        image="/images/installation-cta.webp"
        overlayStyle="linear-gradient(to right, rgba(8,18,35,0.78) 0%, rgba(8,18,35,0.66) 30%, rgba(8,18,35,0.57) 55%, rgba(8,18,35,0.46) 80%, rgba(8,18,35,0.40) 100%)"
      />
    </>
  );
}
